import type { Story } from '@/domain/story'

// P4 seed content for the Interactive Story Engine (Document 11 §P4). One
// full branching story, built entirely as data — StoryPlayerPage renders
// whichever node is current without knowing anything about phishing
// specifically. Adding a second story later means adding another entry
// here, not touching the player.
export const seedStories: Story[] = [
  {
    id: 'story-phishing-urgent-verify',
    title: "Sarah's Inbox: The Urgent Verification",
    topic: 'phishing',
    synopsis:
      'A suspicious "urgent" message lands in Sarah\'s inbox. You make every decision — verify, click, reply, or ignore — and see exactly where each path leads.',
    relatedEpisodeId: 'ep-phishing-trap',
    startNodeId: 'start',
    nodes: {
      start: {
        id: 'start',
        speaker: 'Narrator',
        text: "It's a normal Tuesday. Sarah is catching up on messages when one stands out from the rest, marked urgent.",
        sarahNote: "I'll be right here with you through this one — you make every call.",
        nextNodeId: 'decision1',
      },
      decision1: {
        id: 'decision1',
        speaker: 'Message',
        text: '"URGENT: Your account will be suspended today. Verify immediately using the link below." What should Sarah do?',
        choices: [
          { id: 'a', text: 'Click the link right away, before the deadline', nextNodeId: 'risky-click', isRisky: true },
          { id: 'b', text: 'Reply to the message asking if this is real', nextNodeId: 'risky-reply', isRisky: true },
          { id: 'c', text: "Ignore the link and verify through the official app or website instead", nextNodeId: 'safe1' },
          { id: 'd', text: 'Ignore the whole message and move on', nextNodeId: 'risky-ignore', isRisky: true },
        ],
      },
      'risky-click': {
        id: 'risky-click',
        speaker: 'Narrator',
        text: 'The link opens a page that looks exactly like the real login screen. Sarah types her username and password — and a moment later, the page just... reloads. Nothing seems to happen. That silence is the problem: the page was fake, and it just captured her credentials.',
        sarahNote:
          "This is exactly how phishing works — the fake page looks perfect because it's copied from the real one. The only way to know for sure is to never get there from a link in a message at all.",
        choices: [
          { id: 'retry', text: 'Go back and choose differently', nextNodeId: 'decision1' },
          { id: 'continue', text: 'See what happens next', nextNodeId: 'bad-ending' },
        ],
      },
      'risky-reply': {
        id: 'risky-reply',
        speaker: 'Narrator',
        text: "Sarah replies asking if the message is legitimate. She never hears back from \"the bank\" — but a few days later, the scam messages start arriving more often. Replying confirmed her address was active and being read, which made her a more attractive target, not a safer one.",
        sarahNote: 'Replying — even just to ask questions — tells a scammer the address is real and being read. Silence and verifying independently is always safer.',
        choices: [
          { id: 'retry', text: 'Go back and choose differently', nextNodeId: 'decision1' },
          { id: 'continue', text: 'See what happens next', nextNodeId: 'bad-ending' },
        ],
      },
      'risky-ignore': {
        id: 'risky-ignore',
        speaker: 'Narrator',
        text: "Sarah ignores the message entirely and moves on with her day. It turns out to be a fake this time — but she never actually checked. If it had been real, she'd have missed it completely, with no way to tell the difference next time either.",
        sarahNote:
          "Ignoring isn't dangerous the way clicking is, but it doesn't build the habit that protects you either. Quickly verifying through an official channel takes the guesswork out completely.",
        choices: [
          { id: 'retry', text: 'Go back and choose differently', nextNodeId: 'decision1' },
          { id: 'continue', text: 'See what happens next', nextNodeId: 'bad-ending' },
        ],
      },
      safe1: {
        id: 'safe1',
        speaker: 'Narrator',
        text: "Sarah opens her banking app directly — not through the link — and checks her account status. Everything is completely normal. No suspension, no urgent issue. The message was fake.",
        sarahNote: "That's the move. Verifying through a channel you already trust — never a link in the message itself — is what actually protects you.",
        nextNodeId: 'decision2',
      },
      decision2: {
        id: 'decision2',
        speaker: 'Narrator',
        text: 'Now that Sarah knows the message was fake, what should she do with it?',
        choices: [
          { id: 'report', text: 'Report it as phishing, then delete it', nextNodeId: 'good-ending' },
          { id: 'delete-only', text: 'Just delete it and move on', nextNodeId: 'mild-consequence', isRisky: true },
        ],
      },
      'mild-consequence': {
        id: 'mild-consequence',
        speaker: 'Narrator',
        text: "Deleting it removes the message from Sarah's inbox, but it does nothing to stop the same message from reaching someone else who might not spot the trap.",
        sarahNote: 'Reporting takes a few extra seconds and helps get the sender blocked or flagged for everyone else too — small effort, real impact.',
        choices: [
          { id: 'retry', text: 'Go back and choose differently', nextNodeId: 'decision2' },
          { id: 'continue', text: 'Finish this way instead', nextNodeId: 'good-ending-soft' },
        ],
      },
      'good-ending': {
        id: 'good-ending',
        speaker: 'Narrator',
        text: "Sarah verified through an official channel, confirmed the message was fake, and reported it. No credentials lost, no accounts compromised — and one less phishing message in circulation for everyone else.",
        sarahNote: "That's a clean run. Verify first, report what's fake — every time.",
        isEnding: true,
        outcome: 'good',
      },
      'good-ending-soft': {
        id: 'good-ending-soft',
        speaker: 'Narrator',
        text: 'Sarah verified through an official channel and confirmed the message was fake. She deleted it without reporting it — safe for her, but the message is still out there for someone else to fall for.',
        sarahNote: "You made the safe call for yourself. Next time, try reporting too — it protects people beyond just you.",
        isEnding: true,
        outcome: 'good',
      },
      'bad-ending': {
        id: 'bad-ending',
        speaker: 'Narrator',
        text: "Because Sarah acted on the message instead of verifying first, the attacker got what they were after. Recovering from this means changing passwords immediately, enabling multi-factor authentication, and watching every account closely for weeks.",
        sarahNote: "This is exactly why \"The Phishing Trap\" episode matters — the whole attack works on urgency and a shortcut around verification. Want to try this story again with a different choice?",
        isEnding: true,
        outcome: 'risky',
      },
    },
  },
]
